import { MapLayersController } from './map/map-layers.controller';
import { MapTracesGeoController } from './map/map-traces-geo.controller';
import { MapHistoryController } from './map/map-history.controller';
import { MapDrawInteractionController } from './map/map-draw-interaction.controller';
import { MapHintController, MapHintMode } from './map/map-hint.controller';
import { MapFeatureFocusController } from './map/map-feature-focus.controller';
import { withLiveFields } from './map/map-live-fields';
import { MapPointsController } from './map/map-points.controller';
import {
  MapGeoJsonIOContext,
  downloadAllPolygonsAsGeoJSON,
  handleUpload,
  addGeoJsonToDraw,
  trySimplifyIfLarge,
} from './map/map-geojson-io';
import { MapPopupEditorContext, openCoordsPopup } from './map/map-popup-editors';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../common/confirm-dialog.component';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../common/service/notification.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';

import * as mapboxgl from 'mapbox-gl';
import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';

import { environment } from '../../../environments/environment';
import { ensureMapboxWorker } from '../../common/mapbox-worker';
import { TranslationService } from '../../common/service/translation-service.service';

import type { Feature, FeatureCollection, GeoJsonProperties, Geometry, Polygon as GJPolygon } from 'geojson';
import { GeoUploadService } from '../../common/service/geo-upload.service';
import { DrawValidationService } from './map/services/draw-validation.service';
import { addMapSearchControls, MapSearchControlsHandle, MapSearchRenderedTexts } from './map/map-search-controls';
import { resolveSearchTexts } from './map/map-search-texts';
import { buildMapList } from './map/map-polygon-list';
import { firstValueFrom } from 'rxjs';
import { InfoTextBoxComponent, wikiUrl } from '../../common/info-text-box.component';

import { MapToolsControl } from './map/map-tools.control';
import { MapSelectData, MapSelectResult } from './map/map-select.models';
export type { MapSelectData, MapSelectResult } from './map/map-select.models';
import { EMPTY_FC } from './map/map-editor.constants';
import { idle, debounce, throttle } from './map/map-editor-scheduling';
import { createEditorDraw, createEditorMap } from './map/map-editor-setup';
import { findInvalidCoord, ringLeavesLongitudeRange } from './map/map-geometry.utils';
import { buildCircleFeature, convertPointFeatures } from './map/map-circle-points';
import { findPolygonErrors, largeDatasetReason, overlapAreaM2 } from './map/map-polygon-validation';
import { pointsWithInvalidArea } from './map/map-point.models';
import { allPolygons, enforceLeftHandRules, replacePolygonRing, selectedIds, siteGeometry } from './map/map-draw-edits';
import { buildSaveResult } from './map/map-save-result';
import { buildToolsLabels } from './map/map-tools-labels';
import { defaultOverlayState, OverlayState } from './map/forest-layers';
import { MapOverlaysController } from './map/map-overlays.controller';
import { LayersPanelComponent } from './map/layers-panel.component';
import { MapElementControl } from './map/map-element-control';
import { whispGeometryKey } from './map/whisp-geometry-key';
import { isWhispSettled, riskPartsToLineFeatures, WhispChipState, whispChipState } from './map/whisp-layer';
import { WhispFeatureLookup, whispFeatureLookup } from './map/whisp-feature-findings';
import { createWhispFeatureDetail } from './map/whisp-feature-detail';
import { WhispCheckService } from './map/whisp-check.service';
import { WhispDebugService } from './map/whisp-debug.service';
import { WhispDebugPanelComponent } from './map/whisp-debug-panel.component';
import { AppAuthService } from '../../common/auth/app-auth.service';
import { watchWhisp } from './map/whisp-poll';
import { CompanyWriteAccess } from '../../common/service/company-write-access.service';
import type { WhispSiteView } from '../../model';

type IOLiveField = 'isUploading' | 'uploadProgress' | 'hintText' | 'hasUnsavedChanges';

@Component({
  selector: 'app-map-select-dialog',
  imports: [CommonModule, MatDialogModule, TranslateModule, MatIconModule, LayersPanelComponent, WhispDebugPanelComponent],
  templateUrl: './map-select-dialog.component.html',
  styleUrls: ['./map-select-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
})
export class MapSelectDialogComponent implements OnInit, OnDestroy {
  private readonly data = inject<MapSelectData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<MapSelectDialogComponent>, { optional: true });
  private readonly dialog = inject(MatDialog);
  private readonly snack = inject(NotificationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly geoUpload = inject(GeoUploadService);
  private readonly translation = inject(TranslationService);
  private readonly translate = inject(TranslateService);
  private drawValidation = inject(DrawValidationService);
  private readonly whispCheck = inject(WhispCheckService);
  private readonly writeAccess = inject(CompanyWriteAccess);

  private readonly points = new MapPointsController({
    map: () => this.map,
    draw: () => this.draw,
    dialog: this.dialog,
    translate: (key) => this.translate.instant(key),
    changed: () => this.postEditRecompute(),
    hint: (key) => this.hint.show(key, this.translate.instant(key)),
    resetHint: () => this.setHint('idle'),
    select: (id) => this.focus.selectPoint(id),
    beforeOpen: () => this.closeCoordsPopup(),
    detail: (feature) => this.whispDetail(feature),
  });
  private readonly hint = new MapHintController({
    loadText: (key) => this.translation.loadFromBackend(key),
    hasPolygons: () => this.polygonIds.length > 0,
    isDestroyed: () => this.destroyed,
    changed: () => this.cdr.markForCheck(),
  });
  private readonly focus = new MapFeatureFocusController({
    map: () => this.map,
    draw: () => this.draw,
    state: this.interactionState(),
    setHint: (mode) => this.setHint(mode),
    refreshCtrl: () => this.refreshCtrl(),
    scrollToPolygon: (id) => this.toolsCtrl?.scrollToPolygon(id),
    openPointEditor: (id) => this.points.open(id),
    openCoordinates: () => this.openCoordsPopup(),
    showErr: (key, error) => this.showErr(key, error),
  });
  private readonly history = new MapHistoryController({
    draw: () => this.draw,
    marker: () => this.marker,
    isDrawing: () => this.isDrawing,
    isBlocked: () => this.isUploading || this.drawValidation.isRepairing,
    beforeRestore: () => {
      if (this.drawValidation.isBusy) this.drawValidation.cancel();
      this.points.closeEditor();
      this.closeCoordsPopup();
    },
    recompute: () => this.postEditRecompute(),
    unsavedChanged: (dirty) => {
      this.hasUnsavedChanges = dirty;
      this.cdr.markForCheck();
    },
  });
  private readonly drawInteraction = new MapDrawInteractionController({
    map: () => this.map,
    draw: () => this.draw,
    state: this.interactionState(),
    zone: this.zone,
    isStyleReady: () => this.isStyleReady(),
    setHint: (mode) => this.setHint(mode),
    refreshCtrl: () => this.refreshCtrl(),
    markForCheck: () => this.cdr.markForCheck(),
    scrollToPolygon: (id) => this.toolsCtrl?.scrollToPolygon(id),
    undo: () => this.undo(),
    redo: () => this.redo(),
  });
  private readonly polygonsWithErrors = new Set<string>();
  private readonly tracesGeo = new MapTracesGeoController({
    data: this.data,
    runInZone: (update) => this.zone.run(update),
    changed: () => this.onTracesStatusChanged(),
    preview: {
      derive: (site) => firstValueFrom(this.geoUpload.tracesGeometry(site)),
      validate: (traces) => firstValueFrom(this.geoUpload.validateGeometry(traces)),
    },
    findingsChanged: (findings) => this.layers.showLivePrecisionFindings(findings, siteGeometry(this.draw)),
  });
  private readonly layers = new MapLayersController({
    getMap: () => this.map,
    data: this.data,
    tracesGeometry: () => this.tracesGeo.geometry,
    polygonsWithErrors: this.polygonsWithErrors,
    isStyleReady: () => this.isStyleReady(),
    getAllPolygons: () => allPolygons(this.draw),
    getLargeDatasetReason: largeDatasetReason,
    maybeAnnounceLargeDataset: (reason, force) => this.maybeAnnounceLargeDataset(reason, force),
    overlapAreaM2,
    refreshCtrl: () => this.refreshCtrl(),
  });

  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild(LayersPanelComponent, { static: true, read: ElementRef }) layersPanel!: ElementRef<HTMLElement>;
  @ViewChild(WhispDebugPanelComponent, { static: true, read: ElementRef }) whispDebugPanel!: ElementRef<HTMLElement>;

  map!: mapboxgl.Map;
  marker!: mapboxgl.Marker;
  draw!: MapboxDraw;
  toolsCtrl!: MapToolsControl;

  polygonIds: string[] = [];
  polygonRings: [number, number][][] = [];
  polygonAreasM2: number[] = [];

  isDrawing = false;
  isEditing = false;
  isUploading = false;
  uploadProgress = 0;
  uploadStatus = '';
  hasUnsavedChanges = false;
  whispState: WhispChipState | null = this.data?.whisp ? whispChipState(this.data.whisp) : null;
  whispRiskGroupCount = this.data?.whisp?.riskGroupCount ?? 0;
  private whispRiskPartCount = this.data?.whisp?.riskParts?.length ?? 0;

  readonly showWhispDebug = inject(AppAuthService).isAdmin && !!this.data?.siteId;
  private readonly mapOverlays = new MapOverlaysController(() => this.map, inject(WhispDebugService), {
    ...defaultOverlayState(),
    whispDebug: this.showWhispDebug,
  });
  readonly whispDebug = this.mapOverlays.whispDebug;
  private stopWhispWatchFn: (() => void) | null = null;
  private whispEdited = false;
  private whispGeometryAtOpen: string | null = null;
  private whispFindingReleased = false;
  private storedGeojson = this.data?.geojson;
  private whispLookup: WhispFeatureLookup | null = null;
  savingGeometry = false;
  private uploadAbort?: AbortController;
  private destroyed = false;
  private discardConfirmed = false;
  private confirmingClose = false;
  private lastHeavyDatasetNotice: string | null = null;
  private coordsPopup: mapboxgl.Popup | null = null;
  private searchControls: MapSearchControlsHandle | null = null;

  get hintText() {
    return this.hint.text;
  }
  set hintText(value: string | null) {
    this.hint.text = value;
  }
  private onTracesStatusChanged() {
    this.drawValidation.showTracesActivity(this.tracesGeo.status);
    this.cdr.markForCheck();
  }
  get tracesStatus() {
    return this.tracesGeo.status;
  }

  get canCheckWhispNow(): boolean {
    const state = this.whispState;
    return (
      state !== null &&
      !!this.data?.siteId &&
      !this.writeAccess.readOnly() &&
      !this.hasUnsavedChanges &&
      state !== 'queued' &&
      state !== 'running'
    );
  }

  get canSaveAndCheckWhisp(): boolean {
    return (
      this.whispState !== null &&
      !!this.data?.saveGeometry &&
      !this.writeAccess.readOnly() &&
      this.hasUnsavedChanges &&
      !this.isUploading &&
      !this.savingGeometry
    );
  }

  get whispStale(): boolean {
    const unsettled = this.whispState !== null && !isWhispSettled(this.whispState);
    return unsettled || this.whispDebug.view()?.current === false;
  }

  get hasRiskOutline(): boolean {
    return this.whispRiskPartCount > 0;
  }

  get overlays(): OverlayState {
    return this.mapOverlays.state;
  }

  applyOverlays(state: OverlayState) {
    this.mapOverlays.apply(state);
  }

  markWhispPending() {
    if (!this.whispState) return;
    this.whispEdited = true;
    this.stopWhispWatch();
    this.setWhispState('pending');
    this.showWhispInList(null);
  }

  private whispDetail(feature: Feature): HTMLElement | null {
    const finding = this.whispLookup?.(feature);
    return finding ? createWhispFeatureDetail(finding, this.translate) : null;
  }

  private showWhispInList(lookup: WhispFeatureLookup | null) {
    this.whispLookup = lookup;
    this.refreshCtrl();
  }

  releaseTracesHold() {
    if (this.tracesStatus !== 'held') return;
    this.whispFindingReleased = true;
    this.markWhispPending();
    this.refreshTracesPreview();
  }

  private get whispGeometryChanged(): boolean {
    return this.whispGeometryAtOpen !== null && whispGeometryKey(siteGeometry(this.draw)) !== this.whispGeometryAtOpen;
  }

  private get holdsWhispFinding(): boolean {
    return !this.whispFindingReleased && (this.whispState === 'ok' || this.whispState === 'risk');
  }

  async checkWhispNow() {
    const siteId = this.data?.siteId;
    if (!siteId || !this.canCheckWhispNow) return;
    try {
      await this.whispCheck.checkNow(siteId);
    } catch (err) {
      this.showErr('whisp_check_failed_tr', err);
      return;
    }
    this.setWhispState('queued');
    this.startWhispWatch();
  }

  async saveAndCheckWhisp() {
    const save = this.data?.saveGeometry;
    if (!save || !this.canSaveAndCheckWhisp) return;
    this.savingGeometry = true;
    try {
      const result = await this.collectSaveResult();
      if (!result) return;
      await save(result);
      this.afterGeometrySaved(result.geojson);
    } catch (err) {
      this.showErr('site_geometry_save_failed_tr', err);
    } finally {
      this.savingGeometry = false;
      this.cdr.markForCheck();
    }
  }

  private afterGeometrySaved(stored: FeatureCollection) {
    this.storedGeojson = stored;
    this.history.start();
    this.whispGeometryAtOpen = whispGeometryKey(siteGeometry(this.draw));
    this.whispFindingReleased = false;
    this.whispEdited = false;
    this.whispRiskPartCount = 0;
    this.mapOverlays.showWhispRisk([]);
    this.showWhispInList(null);
    this.refreshTracesPreview();
    this.setWhispState('queued');
    this.startWhispWatch();
    this.showMsg('whisp_saved_check_started_tr');
  }

  private setWhispState(state: WhispChipState) {
    this.whispState = state;
    this.drawValidation.showWhispActivity(isWhispSettled(state) ? 'idle' : state);
    this.cdr.markForCheck();
  }

  private showWhisp(view: WhispSiteView) {
    this.whispRiskGroupCount = view.riskGroupCount;
    this.whispRiskPartCount = view.riskParts?.length ?? 0;
    this.setWhispState(whispChipState(view));
    this.mapOverlays.showWhispRisk(riskPartsToLineFeatures(this.storedGeojson, view.riskParts));
    this.showWhispInList(whispFeatureLookup(this.storedGeojson, view));
    this.reloadWhispDebug(view);
  }

  get debugSiteGeo(): FeatureCollection | null {
    return this.storedGeojson ?? null;
  }

  private attachWhispDebug() {
    if (!this.showWhispDebug) return;
    this.map.addControl(new MapElementControl(this.whispDebugPanel.nativeElement), 'top-right');
  }

  private reloadWhispDebug(view: WhispSiteView) {
    const siteId = this.data?.siteId;
    if (!this.showWhispDebug || !siteId || !view.findingId || view.findingId === this.whispDebug.loadedFindingId) return;
    void this.whispDebug.load(siteId);
  }

  private startWhispWatch() {
    const reload = this.data?.reloadWhisp;
    if (!reload || this.whispEdited) return;
    this.stopWhispWatch();
    this.stopWhispWatchFn = watchWhisp({
      fetchView: reload,
      onUpdate: (view) => {
        if (!this.whispEdited) this.zone.run(() => this.showWhisp(view));
      },
      onError: () => this.zone.run(() => this.setWhispState('error')),
    });
  }

  private stopWhispWatch() {
    this.stopWhispWatchFn?.();
    this.stopWhispWatchFn = null;
  }

  private attachWhisp() {
    this.mapOverlays.attachForest();
  }

  private attachWhispRiskOutline() {
    this.mapOverlays.attachWhispRisk();
    if (this.data?.whisp) this.showWhisp(this.data.whisp);
    if (this.whispState !== null && !isWhispSettled(this.whispState)) this.startWhispWatch();
  }

  get canUndo() {
    return this.history.canUndo;
  }
  get canRedo() {
    return this.history.canRedo;
  }

  cancelUpload() {
    this.uploadAbort?.abort();
  }
  undo() {
    this.history.undo();
  }
  redo() {
    this.history.redo();
  }

  canCloseMap(result: unknown): boolean {
    if (result != null || this.discardConfirmed || (!this.hasUnsavedChanges && !this.isUploading)) return true;
    void this.requestClose();
    return false;
  }
  async requestClose() {
    if (this.confirmingClose) return;
    if (this.hasUnsavedChanges || this.isUploading) {
      this.confirmingClose = true;
      try {
        const discard = await this.confirmDialog('420px', '90vw', {
          title: this.translate.instant('Map_unsaved_title_tr'),
          message: this.translate.instant('Map_unsaved_message_tr'),
          button: this.translate.instant('Map_discard_tr'),
          compactActions: true,
        });
        if (!discard) return;
      } finally {
        this.confirmingClose = false;
      }
    }
    this.discardConfirmed = true;
    this.dialogRef?.close();
  }
  @HostListener('window:beforeunload', ['$event'])
  warnBeforeUnload(event: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges || this.isUploading) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  openHelp = () => InfoTextBoxComponent.openHelp(`${wikiUrl}/adding-geodata`, this.translation.language);
  handleUploadClick() {
    try {
      this.fileInput?.nativeElement?.click();
    } catch (e) {
      console.error(e);
    }
  }

  onFileSelected(evt: Event) {
    const input = evt.target as HTMLInputElement;
    const files = input?.files;
    if (files?.length) {
      this.handleUpload(files);
    }
    if (input) input.value = '';
  }

  private interactionState() {
    const component = this;
    return {
      get isDrawing() {
        return component.isDrawing;
      },
      set isDrawing(value) {
        component.isDrawing = value;
      },
      get isEditing() {
        return component.isEditing;
      },
      set isEditing(value) {
        component.isEditing = value;
      },
    };
  }

  private confirmDialog(width: string, maxWidth: string, data: Record<string, unknown>): Promise<boolean> {
    return firstValueFrom(this.dialog.open(ConfirmDialogComponent, { width, maxWidth, data }).afterClosed()).then(
      Boolean
    );
  }

  private showMsg(message: string) {
    try {
      this.snack.success(message);
    } catch (e) {
      console.error(e);
    }
  }
  private showErrWith(message: string, params: Record<string, unknown>) {
    try {
      this.snack.error(message, params);
    } catch (e) {
      console.error(e);
    }
  }
  private showErr(message: string, err?: unknown) {
    console.error(message, err ?? '');
    try {
      this.snack.error(message);
    } catch (e) {
      console.error(e);
    }
  }

  private isStyleReady(): boolean {
    try {
      return !!this.map?.isStyleLoaded();
    } catch {
      return false;
    }
  }

  private closeCoordsPopup() {
    this.coordsPopup?.remove();
    this.coordsPopup = null;
  }

  private getPolygonList() {
    const workerErrors = this.drawValidation.issueFeatureIds;
    return buildMapList({
      polygons: allPolygons(this.draw),
      points: this.points.features(),
      selectedIds: new Set(selectedIds(this.draw)),
      errorIds: (id) => this.polygonsWithErrors.has(id) || workerErrors.has(id),
      areaLabel: this.translate.instant('Map_area_tr'),
      pointLabel: this.translate.instant('Map_point_tr'),
      whisp: this.whispLookup,
    });
  }

  private deletePolygonById(id: string) {
    try {
      this.draw.delete([id]);
    } catch (err) {
      this.showErr('Could_not_delete_polygon_tr', err);
      return;
    }
    this.postEditRecompute();
  }

  private postEditRecompute() {
    this.enforceLeftHandRuleAll();
    this.handleDrawChange();
    this.validateAll(false);
    this.updateErrorHighlights();
    this.drawValidation.recompute();
    this.refreshCtrl();
    this.history.recordEdit();
    this.hasUnsavedChanges = this.history.unsavedChanges;
    this.scheduleTracesPreview();
  }
  private recordImportedEdit() {
    this.history.recordEdit();
    this.scheduleTracesPreview();
  }
  private scheduleTracesPreview() {
    if (this.whispGeometryChanged) {
      if (this.holdsWhispFinding) return this.tracesGeo.hold();
      this.markWhispPending();
    }
    const site = siteGeometry(this.draw);
    this.tracesGeo.scheduleRecompute(() => siteGeometry(this.draw), site.features.length);
  }
  private refreshTracesPreview() {
    this.tracesGeo.recomputeNow(() => siteGeometry(this.draw));
  }
  private throttledRecompute = throttle(() => this.postEditRecompute(), 120);

  private onDrawChanged() {
    this.history.markDrawChanged();
    this.layers.hidePrecisionFindingsAfterEdit();
    this.throttledRecompute();
  }
  private onDrawCreate = () => this.onDrawChanged();
  private onDrawUpdate = () => {
    this.drawInteraction.hideCircleVertices();
    this.onDrawChanged();
  };
  private onDrawDelete = () => this.onDrawChanged();

  private setHint(mode: MapHintMode) {
    this.hint.set(mode);
  }

  private resolveSearchTexts() {
    return resolveSearchTexts((key) => this.translation.loadFromBackend(key));
  }

  async ngOnInit() {
    try {
      const startLng = this.data?.lng ?? 0;
      const startLat = this.data?.lat ?? 20;
      this.marker = new mapboxgl.Marker({ draggable: true }).setLngLat([startLng, startLat]);
      ensureMapboxWorker();
      this.map = createEditorMap(
        environment.mapbox.accessToken,
        this.mapContainer.nativeElement,
        this.marker.getLngLat()
      );
      this.map.on('load', () => this.onMapLoad());
      this.marker.addTo(this.map);
    } catch (err) {
      this.showErr('Map_setup_failed_tr', err);
    }
  }

  private async onMapLoad() {
    try {
      this.map.resize();
      const searchTexts = await this.resolveSearchTexts();
      if (this.destroyed) return;
      this.attachWhisp();
      this.addControls(searchTexts);
      this.attachDraw();
      this.attachToolsControl();
      this.attachValidation();
      this.attachDrawEvents();
      this.layers.initErrorHighlightLayers();
      this.attachWhispRiskOutline();
      this.tracesGeo.attach(this.map);
      this.layers.renderExternalFindings();
      if (!(await this.loadSavedGeoJson())) return;
      this.whispGeometryAtOpen = whispGeometryKey(siteGeometry(this.draw));
      this.startHistory();
      this.refreshTracesPreview();
      this.drawInteraction.startStrokeAnimationWhenReady();
      this.refreshCtrl();
      this.cdr.markForCheck();
    } catch (err) {
      if (!this.destroyed) this.showErr('Map_failed_to_initialize_tr', err);
    }
    this.syncListHeightWithMap();
  }

  private addControls(searchTexts: Partial<MapSearchRenderedTexts>) {
    this.searchControls = addMapSearchControls(this.map, {
      accessToken: environment.mapbox.accessToken,
      center: { lat: this.data?.lat, lng: this.data?.lng },
      language: this.translation.language,
      texts: searchTexts,
      onError: (messageKey) => this.showErr(messageKey),
    });
    this.map.addControl(new mapboxgl.NavigationControl());
    this.map.addControl(new MapElementControl(this.layersPanel.nativeElement), 'bottom-right');
    this.attachWhispDebug();
    this.setHint('idle');
  }

  private attachDraw() {
    this.draw = createEditorDraw();
    this.map.addControl(this.draw, 'top-left');
  }

  private attachToolsControl() {
    this.toolsCtrl = new MapToolsControl({
      ...buildToolsLabels(this.translate),
      startPolygon: () => this.startPolygon(),
      startCircle: () => this.points.open(),
      clearPolygon: () => this.clearAllAfterConfirm(),
      deletePolygonById: (id: string) => this.deletePolygonById(id),
      getState: () => ({
        isDrawing: this.isDrawing,
        isEditing: this.isEditing,
        hasPolygon: this.draw.getAll().features.length > 0,
      }),
      getPolygonList: () => this.getPolygonList(),
      focusZoomEdit: (id: string) => this.focus.focusZoomEdit(id),
      focusZoomEditAndShowCoords: (id: string) => this.focus.focusZoomEditAndShowCoords(id),
    });
    this.map.addControl(this.toolsCtrl, 'top-left');
  }

  private async clearAllAfterConfirm() {
    const confirmed = await this.confirmDialog('420px', '90vw', {
      title: this.translate.instant('Map_clear_all_tr'),
      message: this.translate.instant('Map_clear_all_confirm_tr'),
      button: this.translate.instant('Map_remove_all_action_tr'),
      compactActions: true,
    });
    if (!confirmed || !this.draw) return;
    this.points.cancelPicking();
    this.closeCoordsPopup();
    this.draw.deleteAll();
    this.postEditRecompute();
    this.drawValidation.recompute();
  }

  private attachValidation() {
    this.drawValidation.attach(this.map, this.draw, {
      onStateChange: () => {
        this.toolsCtrl?.refresh();
        this.cdr.markForCheck();
      },
      onAfterFix: () => {
        try {
          this.postEditRecompute();
        } catch {}
      },
    });
  }

  private attachDrawEvents() {
    this.map.on('draw.create', this.onDrawCreate);
    this.map.on('draw.update', this.onDrawUpdate);
    this.map.on('draw.delete', this.onDrawDelete);
    this.drawInteraction.attach();
    this.drawInteraction.hideCircleVertices();
  }

  private async loadSavedGeoJson(): Promise<boolean> {
    const geojson = this.data?.geojson;
    if (!geojson) return true;
    if (findInvalidCoord(geojson)) {
      this.showErr('Invalid_coordinates_in_initial_GeoJSON_tr');
      return true;
    }
    await this.addGeoJsonToDraw(geojson);
    if (this.destroyed) return false;
    this.fitAllLater();
    this.updateErrorHighlights();
    this.drawValidation.recompute();
    return true;
  }

  private startHistory() {
    this.history.start();
    this.marker.on('dragend', () => this.history.recordEdit());
  }

  private syncListHeightWithMap() {
    try {
      const syncHeights = () => this.toolsCtrl?.syncListHeightToMap(this.map);
      this.map.on('resize', syncHeights);
      syncHeights();
    } catch {}
  }

  private updateErrorHighlights() {
    this.layers.updateErrorHighlights();
  }

  private maybeAnnounceLargeDataset(reason: string | null, force = false) {
    if (!reason) {
      this.lastHeavyDatasetNotice = null;
      return;
    }
    if (force || reason !== this.lastHeavyDatasetNotice) {
      this.showMsg(reason);
      this.lastHeavyDatasetNotice = reason;
    }
  }

  startPolygon(): void {
    this.points.closeEditor();
    if (!this.draw) return;
    this.isDrawing = true;
    try {
      this.map.getCanvas().style.cursor = 'crosshair';
      this.map.doubleClickZoom.disable();
      (this.draw as any).changeMode('draw_polygon');
    } catch (err) {
      this.showErr('Could_not_start_drawing_mode_tr', err);
    }
    this.setHint('draw');
    this.refreshCtrl();
    this.cdr.markForCheck();
    this.focus.focusCanvas();
  }

  private addCircleToDraw(lng: number, lat: number, radiusHa: number, name = ''): string | null {
    try {
      const feat = buildCircleFeature(lng, lat, radiusHa, { name });
      if (ringLeavesLongitudeRange((feat.geometry.coordinates?.[0] ?? []) as [number, number][])) {
        this.showErr('Map_circle_crosses_antimeridian_tr');
        return null;
      }
      const newId = (this.draw as any).add(feat as any) as string | string[];
      const id = Array.isArray(newId) ? newId[0] : newId;
      try {
        (this.draw as any).changeMode('simple_select', { featureIds: [id] });
      } catch {}
      this.postEditRecompute();
      this.focus.zoomTo(id);
      return id;
    } catch (err) {
      this.showErr('Could_not_create_circle_tr', err);
      return null;
    }
  }

  private replacePolygon(id: string, newRing: [number, number][], properties?: Record<string, any>): string | null {
    try {
      const nextId = replacePolygonRing(this.draw, id, newRing, properties);
      if (nextId) this.postEditRecompute();
      return nextId;
    } catch (err) {
      this.showErr('Could_not_update_the_polygon_tr', err);
      return null;
    }
  }

  clearPolygon(): void {
    if (!this.draw) return;
    const sel = selectedIds(this.draw);
    if (sel.length) {
      try {
        this.draw.delete(sel);
      } catch (err) {
        this.showErr('Could_not_delete_selected_polygon_tr', err);
      }
    }
    this.postEditRecompute();
  }

  private refreshCtrl() {
    try {
      this.toolsCtrl?.refresh();
    } catch {}
  }

  private handleDrawChange(): void {
    this.points.refresh();
    const polys = allPolygons(this.draw);
    if (!polys.length) {
      this.resetPolygonState();
      this.cdr.markForCheck();
      return;
    }
    this.polygonIds = polys.map((f) => (f as any).id as string);
    this.polygonRings = polys.map((f: any) => (f.geometry.coordinates?.[0] ?? []) as [number, number][]);
    this.polygonAreasM2 = polys.map((p) => {
      try {
        return turf.area(p as any);
      } catch {
        return 0;
      }
    });
    this.cdr.markForCheck();
  }

  private resetPolygonState(): void {
    this.polygonIds = [];
    this.polygonRings = [];
    this.polygonAreasM2 = [];
    this.isDrawing = false;
    this.isEditing = false;
  }

  private enforceLeftHandRuleAll(): void {
    enforceLeftHandRules(this.draw, (err) => this.showErr('Failed_to_enforce_ring_orientation_on_a_polygon_tr', err));
  }

  private get geoJsonIOContext(): MapGeoJsonIOContext {
    const context: Omit<MapGeoJsonIOContext, IOLiveField> = {
      draw: this.draw,
      map: this.map,
      cdr: this.cdr,
      geoUpload: this.geoUpload,
      translation: this.translation,
      drawValidation: this.drawValidation,
      uploadSignal: this.uploadAbort?.signal,
      loadingProgress: (completed, total) => {
        this.uploadStatus = this.translate.instant('Map_loading_progress_tr', { completed, total });
        this.cdr.markForCheck();
      },
      showMsg: (key) => this.showMsg(key),
      showErr: (key, error) => this.showErr(key, error),
      convertPointFeatures,
      enforceLeftHandRuleAll: () => this.enforceLeftHandRuleAll(),
      handleDrawChange: () => this.handleDrawChange(),
      validateAll: (notify) => this.validateAll(notify),
      updateErrorHighlights: () => this.updateErrorHighlights(),
      refreshCtrl: () => this.refreshCtrl(),
      recordHistory: () => this.recordImportedEdit(),
    };
    return withLiveFields<MapGeoJsonIOContext, IOLiveField>(context, this, [
      'isUploading',
      'uploadProgress',
      'hintText',
      'hasUnsavedChanges',
    ]);
  }

  downloadAllPolygonsAsGeoJSON(): void {
    downloadAllPolygonsAsGeoJSON.call(this.geoJsonIOContext);
  }

  private async handleUpload(files: FileList) {
    if (this.isUploading || this.drawValidation.isBusy) return;
    this.uploadAbort = new AbortController();
    try {
      await handleUpload.call(this.geoJsonIOContext, files);
    } finally {
      this.uploadAbort = undefined;
      this.uploadStatus = '';
    }
  }

  private addGeoJsonToDraw(input: any) {
    return addGeoJsonToDraw.call(this.geoJsonIOContext, input);
  }

  private fitAllLater = debounce(() => this.fitToAll(), 120);

  private async fitToAll() {
    try {
      await idle(50);
      if (this.destroyed) return;
      this.focus.fitBoundsOf(this.draw.getAll() as FeatureCollection);
    } catch (err) {
      if (this.destroyed) return;
      this.showErr('fitAllLater_failed_due_to_invalid_coordinates_Please_fix_your_data_and_try_again_tr', err);
      console.warn('fitAllLater failed:', err);
    }
  }

  private async validateAll(notify = false): Promise<boolean> {
    const polys = allPolygons(this.draw);
    const skipReason = largeDatasetReason(polys);
    this.maybeAnnounceLargeDataset(skipReason, notify && !!skipReason);
    const errors = [...this.pointAreaErrors(), ...(skipReason ? [] : findPolygonErrors(polys))];
    if (notify && errors.length)
      this.showErr(`${await this.translation.loadFromBackend('Validation_failed_tr')} '\n• ${errors.join('\n•')}'`);
    else if (notify && !skipReason) this.showMsg('Polygons_validated_OK_and_Saved_tr');
    return errors.length === 0;
  }

  private pointAreaErrors(): string[] {
    const features = (this.draw?.getAll()?.features ?? []) as Feature[];
    return pointsWithInvalidArea(features).map((name) => this.translate.instant('Map_point_area_invalid_tr', { name }));
  }

  async saveAndClose(): Promise<void> {
    try {
      const result = await this.collectSaveResult();
      if (!result) return;
      this.hasUnsavedChanges = false;
      this.dialogRef?.close(result);
    } catch (err) {
      this.showErr('Save_failed_Dataset_may_be_too_large_or_invalid_tr', err);
    }
  }

  private async collectSaveResult(): Promise<MapSelectResult | null> {
    this.enforceLeftHandRuleAll();
    this.handleDrawChange();
    const ok = await this.validateAll(true);
    this.updateErrorHighlights();
    if (!ok) return null;

    const drawGeoJson = (this.draw?.getAll() as FeatureCollection<Geometry, GeoJsonProperties>) ?? EMPTY_FC;
    const summary = buildSaveResult({
      drawGeoJson,
      markerPoint: this.marker.getLngLat(),
      points: this.points.features(),
      polygonRings: this.polygonRings,
      polygonAreasM2: this.polygonAreasM2,
    });
    const { result, simplified, beforePts, afterPts, tolUsed } = trySimplifyIfLarge(drawGeoJson as FeatureCollection);
    if (simplified)
      this.showMsg(`Large dataset simplified (${beforePts}→${afterPts} pts${tolUsed ? `, tol=${tolUsed}` : ''}).`);
    return <MapSelectResult>{ ...summary, geojson: result };
  }

  private getActivePolygon(): { id: string; feature: Feature<GJPolygon>; ring: [number, number][] } | null {
    const sel = selectedIds(this.draw);
    const polys = allPolygons(this.draw);
    const target = sel.length ? polys.find((p) => (p as any).id === sel[0]) : polys[0];
    if (!target) return null;
    const id = (target as any).id as string;
    const ring = ((target.geometry.coordinates?.[0] ?? []) as any[]).map((p) => [p[0], p[1]] as [number, number]);
    return { id, feature: target, ring };
  }

  private get popupEditorContext(): MapPopupEditorContext {
    const context: Omit<MapPopupEditorContext, 'coordsPopup'> = {
      map: this.map,
      marker: this.marker,
      draw: this.draw,
      translate: this.translate,
      showErr: (key, error) => this.showErr(key, error),
      showErrWith: (key, params) => this.showErrWith(key, params),
      showMsg: (key) => this.showMsg(key),
      getActivePolygon: () => this.getActivePolygon(),
      buildCircleFeature,
      replacePolygon: (id, ring, properties) => this.replacePolygon(id, ring, properties),
      addCircleToDraw: (lng, lat, radiusHa, name) => this.addCircleToDraw(lng, lat, radiusHa, name),
      zoomToPolygon: (id) => this.focus.zoomTo(id),
      refreshCtrl: () => this.refreshCtrl(),
      whispDetail: (feature) => this.whispDetail(feature),
      confirmLargePolygon: (count) =>
        this.confirmDialog('440px', '95vw', {
          title: this.translate.instant('Map_large_polygon_title_tr'),
          message: this.translate.instant('Map_large_polygon_warning_tr', { count }),
          button: this.translate.instant('Map_continue_editing_tr'),
        }),
    };
    return withLiveFields<MapPopupEditorContext, 'coordsPopup'>(context, this, ['coordsPopup']);
  }

  private openCoordsPopup() {
    this.points.closeEditor();
    openCoordsPopup.call(this.popupEditorContext);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.tracesGeo.destroy();
    this.stopWhispWatch();
    this.throttledRecompute.cancel();
    this.fitAllLater.cancel();
    this.uploadAbort?.abort();
    this.points.destroy();
    this.drawInteraction.detach();
    try {
      if (this.map) {
        this.map.off('draw.create', this.onDrawCreate);
        this.map.off('draw.update', this.onDrawUpdate);
        this.map.off('draw.delete', this.onDrawDelete);
        this.searchControls?.remove();
        this.searchControls = null;
        if (this.toolsCtrl) this.map.removeControl(this.toolsCtrl);
        this.drawValidation.detach();
        this.map.remove();
      }
    } catch (err) {
      this.showErr('Map_cleanup_failed_tr', err);
    }
  }
}
